import { clsx } from "clsx";
import { X } from "lucide-react";
import type { HTMLAttributes } from "react";

/**
 * A removable chip for groups, tags, relations. A `tone` is a token name from the design system
 * (accent | ok | warn | err | neutral); colour is never the only signal — the chip carries its word.
 */
export function Chip({
  tone: toneProp,
  onRemove,
  removeLabel,
  className,
  children,
  ...props
}: HTMLAttributes<HTMLSpanElement> & { tone?: string | null; onRemove?: () => void; removeLabel?: string }) {
  const tone = toneProp ?? "neutral";
  return (
    <span
      className={clsx(
        "inline-flex h-[26px] items-center gap-1 rounded-sm border px-2 text-[12.5px] font-medium",
        tone === "accent" && "border-accent-tint-2 bg-accent-tint text-accent",
        tone === "ok" && "border-transparent bg-ok-tint text-ok",
        tone === "warn" && "border-transparent bg-warn-tint text-warn",
        tone === "err" && "border-transparent bg-err-tint text-err",
        tone === "neutral" &&
          "border-neutral-200 bg-neutral-100 text-neutral-700 dark:border-neutral-800 dark:bg-neutral-800 dark:text-neutral-200",
        className,
      )}
      {...props}
    >
      {children}
      {onRemove && (
        <button
          type="button"
          aria-label={removeLabel ?? "remove"}
          onClick={onRemove}
          className="-mr-1 grid h-5 w-5 place-items-center rounded-sm text-current opacity-60 hover:bg-[rgba(28,27,26,0.08)] hover:opacity-100"
        >
          <X size={12} aria-hidden />
        </button>
      )}
    </span>
  );
}
